import { Copy } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { buildPairedJsonDiffRows } from '@/services/bigQuerySyncPreview/buildPairedJsonDiffRows';
import { formatUnknownJson } from '@/services/bigQuerySyncPreview/formatUnknownJson';
import { cn } from '@/lib/utils';

type BigQuerySyncDiffBeforeAfterPairProps = {
  beforeJson: unknown;
  afterJson: unknown;
};

export function BigQuerySyncDiffBeforeAfterPair({
  beforeJson,
  afterJson,
}: BigQuerySyncDiffBeforeAfterPairProps) {
  const [copied, setCopied] = useState<'before' | 'after' | null>(null);

  const leftText = formatUnknownJson(beforeJson);
  const rightText = formatUnknownJson(afterJson);

  if (!leftText && !rightText) {
    return <span className="text-muted-foreground">—</span>;
  }

  const rows = buildPairedJsonDiffRows(leftText, rightText);

  const copySide = async (side: 'before' | 'after') => {
    const text = side === 'before' ? leftText : rightText;
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(side);
      window.setTimeout(() => setCopied(null), 2000);
    } catch {
      setCopied(null);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 gap-1 px-2 text-xs"
          disabled={!leftText}
          onClick={() => void copySide('before')}
        >
          <Copy className="size-3" aria-hidden />
          {copied === 'before' ? 'Antes copiado' : 'Copiar antes'}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 gap-1 px-2 text-xs"
          disabled={!rightText}
          onClick={() => void copySide('after')}
        >
          <Copy className="size-3" aria-hidden />
          {copied === 'after' ? 'Depois copiado' : 'Copiar depois'}
        </Button>
      </div>

      <div className="max-h-[min(28rem,52vh)] overflow-auto rounded-md border border-border/80 bg-muted/10 shadow-inner">
        <table className="w-full min-w-[42rem] border-collapse text-left font-mono text-xs leading-relaxed">
          <thead className="sticky top-0 z-10 bg-muted/95 shadow-sm backdrop-blur-sm">
            <tr className="border-b border-border">
              <th className="w-1/2 px-2 py-2 font-semibold text-foreground">Antes</th>
              <th className="w-1/2 border-l border-border px-2 py-2 font-semibold text-foreground">
                Depois
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={2} className="px-3 py-6 text-center text-sm text-muted-foreground">
                  Sem linhas para comparar.
                </td>
              </tr>
            ) : (
              rows.map((row, i) => (
                <tr key={i} className="border-b border-border/40 align-top odd:bg-background/40">
                  <td
                    className={cn(
                      'whitespace-pre-wrap break-all px-2 py-1.5 align-top',
                      row.highlightLeft &&
                        'border-l-2 border-rose-500/55 bg-rose-500/12 text-foreground dark:bg-rose-500/20'
                    )}
                  >
                    {row.left || (row.right ? '\u00a0' : '')}
                  </td>
                  <td
                    className={cn(
                      'whitespace-pre-wrap break-all border-l border-border/60 px-2 py-1.5 align-top',
                      row.highlightRight &&
                        'border-l-2 border-emerald-600/55 bg-emerald-500/12 text-foreground dark:bg-emerald-500/20'
                    )}
                  >
                    {row.right || (row.left ? '\u00a0' : '')}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <p className="text-[0.65rem] leading-snug text-muted-foreground">
        Destaque: removido/alterado no antes (rosa), adicionado/alterado no depois (verde). Uma
        única área de scroll mantém as duas colunas alinhadas.
      </p>
    </div>
  );
}
